/**
 * Bookmark search in the top bar, with a Cmd/Ctrl-K shortcut.
 *
 * The query only runs once there is a term, using `"skip"` so an empty box
 * costs nothing — Convex subscriptions are live, and a search over every
 * bookmark on every keystroke of an empty field would be pure waste.
 */
import { useEffect, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { Search, ExternalLink } from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");

  const results = useQuery(
    api.items.search,
    term.trim() ? { term } : "skip",
  );
  const touch = useMutation(api.items.touch);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const openItem = (id: string, url: string) => {
    // Record the visit, but never let a failed write block the navigation.
    touch({ id: id as never }).catch(() => {});
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
    setTerm("");
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 h-9 px-3 rounded-lg border border-border bg-muted/40 text-muted-foreground text-sm hover:bg-muted transition-colors min-w-0 flex-1 max-w-xs"
      >
        <Search className="w-4 h-4 shrink-0" />
        <span className="truncate">Search bookmarks</span>
        <kbd className="ml-auto hidden lg:inline text-[10px] font-mono px-1.5 py-0.5 rounded border border-border bg-background">
          ⌘K
        </kbd>
      </button>

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title="Search bookmarks"
        /* Convex already filtered; cmdk's own filter would additionally drop
           items matched on description or tags. */
        shouldFilter={false}
      >
        <CommandInput
          placeholder="Search bookmarks…"
          value={term}
          onValueChange={setTerm}
        />
        <CommandList>
          {term.trim() && results?.length === 0 && (
            <CommandEmpty>No bookmarks match “{term}”.</CommandEmpty>
          )}
          {!term.trim() && (
            <CommandEmpty>Start typing to search your bookmarks.</CommandEmpty>
          )}
          {results && results.length > 0 && (
            <CommandGroup heading="Bookmarks">
              {results.map((item) => (
                <CommandItem
                  key={item._id}
                  value={`${item.name} ${item.url}`}
                  onSelect={() => openItem(item._id, item.url)}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="truncate">{item.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {item.url}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
